import React, { useCallback, useEffect, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import { useSelector } from 'react-redux'
import { BrowserRouter as Router } from 'react-router-dom'

import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr'

import authService from './api/authApi'
import { App } from './components/App'
import i18n from './localization/i18next'
import { setConfig } from './redux/actions/configActions'
import { useAppDispatch } from './redux/configureStore'
import { IRootState } from './redux/reducers'
import { ConfigurationState } from './redux/reducers/initialState'
import { getMsalConfig, isConfigEmpty } from './config'
import { getServerBaseUrl } from './envConfig'

function AppInitializer() {
  const dispatch = useAppDispatch()
  const configuration: ConfigurationState | null = useSelector(
    (state: IRootState) => state.configuration,
  )
  const [hubConnection, setHubConnection] = useState<HubConnection | undefined>(
    undefined,
  )
  const [msalInstance, setMsalInstance] = useState<
    PublicClientApplication | undefined
  >(undefined)

  const establishSignalrHubConnection = useCallback(async () => {
    const serverBaseUrl = getServerBaseUrl()
    const configConn = new HubConnectionBuilder()
      .withUrl(`${serverBaseUrl}/Config`, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .build()

    configConn.on('ReceiveAppConfiguration', (response: ConfigurationState) => {
      dispatch(setConfig(response))
    })

    if (
      !process.env.JEST_WORKER_ID && // Check if not in Jest environment. We do not want to start the hubConnection when running unit test
      (!hubConnection || hubConnection.state !== HubConnectionState.Connected)
    ) {
      await configConn.start()
      setHubConnection(configConn)
    }
  }, [hubConnection])

  useEffect(() => {
    establishSignalrHubConnection()
  }, [])

  useEffect(() => {
    if (!hubConnection) return
    const handler = (response: ConfigurationState) => {
      dispatch(setConfig(response))
    }
    hubConnection.on('ReceiveAppConfiguration', handler)

    return () => {
      hubConnection.off('ReceiveAppConfiguration', handler)
    }
  }, [hubConnection, dispatch])

  useEffect(() => {
    async function initializeApp() {
      if (!isConfigEmpty(configuration)) {
        const pcaInstance: PublicClientApplication =
          new PublicClientApplication(getMsalConfig(configuration))
        await pcaInstance.initialize()
        authService.setMsalInstance(pcaInstance)
        setMsalInstance(pcaInstance)
      }
    }

    initializeApp()
  }, [configuration])

  useEffect(() => {
    try {
      if (
        hubConnection &&
        hubConnection.state === HubConnectionState.Connected
      ) {
        hubConnection
          .invoke('GetAppConfiguration')
          .catch((err: any) =>
            console.error('Error invoking GetAppConfiguration', err),
          )
      }
    } catch (error) {
      throw new Error('Error retrieving configuration data')
    }
  }, [hubConnection])

  return msalInstance === undefined ? (
    <Router>
      <I18nextProvider i18n={i18n}>
        <App />
      </I18nextProvider>
    </Router>
  ) : (
    <MsalProvider instance={msalInstance}>
      <Router>
        <I18nextProvider i18n={i18n}>
          <App />
        </I18nextProvider>
      </Router>
    </MsalProvider>
  )
}

export default AppInitializer
